import type { PoolMathResult, PoolMathUnavailableReason } from './types.js';

export const BPS_DENOMINATOR = 10_000n;
export const FEE_RATE_DENOMINATOR = 1_000_000n;
export const PRICE_SCALE_D9 = 1_000_000_000n;
export const I64_MIN: bigint = -(1n << 63n);
export const I64_MAX: bigint = (1n << 63n) - 1n;
export const U64_MAX: bigint = (1n << 64n) - 1n;
export const U128_MAX: bigint = (1n << 128n) - 1n;
export const U256_MAX: bigint = (1n << 256n) - 1n;

export function available<T>(value: T): PoolMathResult<T> {
  return { available: true, value };
}

export function unavailable<T>(reason: PoolMathUnavailableReason): PoolMathResult<T> {
  return { available: false, reason };
}

export function ceilDivide(numerator: bigint, denominator: bigint): bigint {
  if (numerator === 0n) return 0n;
  return (numerator - 1n) / denominator + 1n;
}

export function isU64(value: bigint): boolean {
  return value >= 0n && value <= U64_MAX;
}

export function isI64(value: bigint): boolean {
  return value >= I64_MIN && value <= I64_MAX;
}

export function isNonNegativeI64(value: bigint): boolean {
  return value >= 0n && value <= I64_MAX;
}

/** Checked domain for account-level sums of independently valid u64 values. */
export function isU128(value: bigint): boolean {
  return value >= 0n && value <= U128_MAX;
}

/** Checked signed-magnitude domain for aggregate account PnL values. */
export function isSignedMagnitudeU128(value: bigint): boolean {
  return value >= -U128_MAX && value <= U128_MAX;
}

/** Adds account-level unsigned values without exceeding the explicit u128 domain. */
export function checkedAddU128(left: bigint, right: bigint): PoolMathResult<bigint> {
  if (!isU128(left) || !isU128(right)) return unavailable('invalid_amount');
  const sum = left + right;
  return isU128(sum) ? available(sum) : unavailable('invalid_amount');
}

/** Checked account total for independently valid cash and holdings-mark sums. */
export function checkedAccountTotalD6(componentsD6: readonly bigint[]): PoolMathResult<bigint> {
  let totalD6 = 0n;
  for (const componentD6 of componentsD6) {
    const next = checkedAddU128(totalD6, componentD6);
    if (!next.available) return next;
    totalD6 = next.value;
  }
  return available(totalD6);
}

export function isU256(value: bigint): boolean {
  return value >= 0n && value <= U256_MAX;
}

export function isPositiveU64(value: bigint): boolean {
  return value > 0n && value <= U64_MAX;
}

export function isRate(value: bigint, denominator: bigint): boolean {
  return value >= 0n && value <= denominator;
}

/**
 * Converts an executable CP trade-fee rate to its nearest whole-BPS display
 * value. CP requires the effective trade rate to remain below its denominator.
 */
export function cpTradeFeeMillionthsToNearestBps(rateMillionths: bigint): PoolMathResult<number> {
  if (!isU64(rateMillionths) || rateMillionths >= FEE_RATE_DENOMINATOR) {
    return unavailable('invalid_fee_config');
  }
  return available(Number((rateMillionths + 50n) / 100n));
}

export function floorFee(amount: bigint, rate: bigint, denominator: bigint): bigint {
  return (amount * rate) / denominator;
}

export function ceilFee(amount: bigint, rate: bigint, denominator: bigint): bigint {
  return ceilDivide(amount * rate, denominator);
}

export interface FloorFeeSplit {
  ratedAmount: bigint;
  remainderAmount: bigint;
}

/** Floor the rated leg and assign the integer remainder to the other leg. */
export function splitFloorFee(amount: bigint, rate: bigint, denominator: bigint): FloorFeeSplit {
  const ratedAmount = floorFee(amount, rate, denominator);
  return { ratedAmount, remainderAmount: amount - ratedAmount };
}
